'use client';

import { useEffect, useState } from 'react';

import type { PopStoreExtendedSubscriptionLevel } from '@ps/models/lib/creator-subscription';

import { deriveSubscriptionState } from '../../subscription/functions/currentUserSubscriptionState';
import { isDmPermitted } from '../../subscription/functions/isDmPermitted';
import { synthesizeExtendedSubscriptionLevel } from '../../subscription/functions/synthesizeExtendedSubscriptionLevel';
import { fetchStoreDmGate } from '../functions/fetchStoreDmGate';
import { useChatContext } from './useChatContext';

export type ChatSendCapability =
  | { status: 'loading' }
  | { status: 'allowed' }
  | {
      status: 'subscription-required';
      minLevel: PopStoreExtendedSubscriptionLevel | undefined;
    }
  | { status: 'disabled' };

/**
 * Whether the signed-in user may send in THIS chat, gated on the DM minimum
 * subscription level — 1:1 with PopshopRN `ChatActionBar.checkChatCapability`.
 * Applies to BOTH ends of a conversation:
 *
 * - customer → creator: `subscription-required` (prompt subscribe) unless the
 *   customer meets the creator's `dmMinimumSubscriptionLevel`.
 * - creator → customer: `disabled` unless the *target* customer meets the
 *   creator's own floor (a creator can't DM an under-subscribed fan).
 * - creator ↔ creator: `allowed` if EITHER side's subscription meets the other.
 *
 * The floor is read FRESH from `stores/<id>` (not the cached bio) so a creator's
 * just-changed level takes effect immediately. Errors fail closed (`disabled`),
 * matching upstream.
 */
export function useChatSendCapability(): ChatSendCapability {
  const { currentUserId, targetUserId } = useChatContext();
  const [capability, setCapability] = useState<ChatSendCapability>({
    status: 'loading',
  });

  useEffect(() => {
    if (!currentUserId || !targetUserId) {
      setCapability({ status: 'disabled' });
      return;
    }
    let disposed = false;
    const controller = new AbortController();
    setCapability({ status: 'loading' });

    void (async () => {
      try {
        const [currentStore, targetStore] = await Promise.all([
          fetchStoreDmGate(currentUserId),
          fetchStoreDmGate(targetUserId),
        ]);
        if (disposed) return;

        // At least one side must be a creator for a popstore DM.
        if (!currentStore.isCreator && !targetStore.isCreator) {
          setCapability({ status: 'disabled' });
          return;
        }

        const { checkUserSubscriptionStatus } =
          await import('../../subscription/functions/checkUserSubscriptionStatus');
        if (disposed) return;

        // Follow-only standing never satisfies `isDmPermitted` (FOLLOWER ranks
        // below every threshold), so we don't need the follow flag here.
        const levelTo = async (userId: string, creatorId: string) => {
          const data = await checkUserSubscriptionStatus({
            userId,
            creatorId,
            signal: controller.signal,
          });
          return synthesizeExtendedSubscriptionLevel(
            deriveSubscriptionState(data).accessLevel,
            false,
          );
        };

        const [subToTarget, subToCurrent] = await Promise.all([
          targetStore.isCreator
            ? levelTo(currentUserId, targetUserId)
            : Promise.resolve(undefined),
          currentStore.isCreator
            ? levelTo(targetUserId, currentUserId)
            : Promise.resolve(undefined),
        ]);
        if (disposed) return;

        const permittedToTarget =
          targetStore.isCreator &&
          isDmPermitted(targetStore.minLevel, subToTarget);
        const permittedToCurrent =
          currentStore.isCreator &&
          isDmPermitted(currentStore.minLevel, subToCurrent);

        if (targetStore.isCreator && !currentStore.isCreator) {
          setCapability(
            permittedToTarget
              ? { status: 'allowed' }
              : {
                  status: 'subscription-required',
                  minLevel: targetStore.minLevel,
                },
          );
        } else if (currentStore.isCreator && !targetStore.isCreator) {
          setCapability(
            permittedToCurrent ? { status: 'allowed' } : { status: 'disabled' },
          );
        } else {
          setCapability(
            permittedToTarget || permittedToCurrent
              ? { status: 'allowed' }
              : {
                  status: 'subscription-required',
                  minLevel: targetStore.minLevel,
                },
          );
        }
      } catch (error) {
        if (disposed || controller.signal.aborted) return;
        console.error('Failed to resolve chat send capability:', error);
        setCapability({ status: 'disabled' });
      }
    })();

    return () => {
      disposed = true;
      controller.abort();
    };
  }, [currentUserId, targetUserId]);

  return capability;
}
